import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Lock, Trash2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { SkeletonCard } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useT } from "@/i18n";

const STATUS_KEYS = ["idea", "da_registrare", "registrato", "da_montare", "pronto", "pubblicato"];
const STATUS_COLOR = {
  idea: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
  da_registrare: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  registrato: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  da_montare: "bg-brand-700/15 text-brand-800 dark:text-brand-600",
  pronto: "bg-brand-500/15 text-brand-700 dark:text-brand-400",
  pubblicato: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
};

export function Calendar() {
  const { t } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", status: "idea", notes: "" });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["calendar"],
    queryFn: async () => (await api.get("/calendar")).data,
    retry: false,
  });

  const locked = isError && error?.response?.status === 403;

  const add = useMutation({
    mutationFn: async () => (await api.post("/calendar", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); setOpen(false); setForm({ title: "", date: "", status: "idea", notes: "" }); toast.success("Contenuto aggiunto"); },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/calendar/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar"] }),
  });
  const updateStatus = useMutation({
    mutationFn: async ({ item, status }) => (await api.put(`/calendar/${item.item_id}`, { ...item, status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar"] }),
  });

  if (locked) {
    return (
      <DashboardLayout>
        <LockedFeature onUpgrade={() => navigate("/pricing")} title={t("calendar.title")} />
      </DashboardLayout>
    );
  }

  const items = (data?.items || []).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("calendar.title")}</h1>
            <p className="mt-1 text-muted-foreground">{t("calendar.subtitle")}</p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="add-calendar-btn" className="rounded-xl bg-brand-500 hover:bg-brand-600 text-black">
            <Plus className="mr-2 h-4 w-4" /> {t("calendar.add")}
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-3">{[0,1,2].map(i => <SkeletonCard key={i} className="h-16" />)}</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <CalendarDays className="mb-3 h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground">Nessun contenuto programmato. Aggiungi la tua prima idea!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="calendar-list">
            {items.map((item) => (
              <div key={item.item_id} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm">
                <div className="w-14 shrink-0 text-center">
                  <div className="font-display text-xl font-bold">{new Date(item.date).getDate()}</div>
                  <div className="text-xs uppercase text-muted-foreground">{new Date(item.date).toLocaleDateString("it-IT", { month: "short" })}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{item.title}</div>
                  {item.notes && <div className="text-sm text-muted-foreground truncate">{item.notes}</div>}
                </div>
                <Select value={item.status} onValueChange={(s) => updateStatus.mutate({ item, status: s })}>
                  <SelectTrigger className={`h-8 w-auto gap-1.5 rounded-full border-0 text-xs font-medium ${STATUS_COLOR[item.status]}`} data-testid={`status-${item.item_id}`}>
                    <SelectValue>{t(`calendar.statuses.${item.status}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>{STATUS_KEYS.map(s => <SelectItem key={s} value={s}>{t(`calendar.statuses.${s}`)}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={() => del.mutate(item.item_id)} data-testid={`del-${item.item_id}`}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="calendar-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("calendar.newItem")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t("calendar.itemTitle")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cal-title" className="rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("common.date")}</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cal-date" className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Stato</Label>
                <Select value={form.status} onValueChange={(s) => setForm({ ...form, status: s })}>
                  <SelectTrigger className="rounded-xl" data-testid="cal-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_KEYS.map(s => <SelectItem key={s} value={s}>{t(`calendar.statuses.${s}`)}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>{t("calendar.notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cal-notes" className="rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => add.mutate()} disabled={!form.title || !form.date || add.isPending} data-testid="cal-save" className="rounded-xl bg-brand-500 hover:bg-brand-600 text-black">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export const LockedFeature = ({ onUpgrade, title }) => {
  const { t } = useT();
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700">
        <Lock className="h-8 w-8 text-white" />
      </div>
      <h2 className="font-display text-2xl font-bold">{title}</h2>
      <p className="mt-2 max-w-sm text-muted-foreground">Questa funzione è disponibile con il piano Pro.</p>
      <Button onClick={onUpgrade} data-testid="feature-upgrade-btn" className="mt-6 rounded-xl bg-brand-500 hover:bg-brand-600 text-black">{t("common.upgrade")}</Button>
    </div>
  );
};
